/**
 * Overlay a Liquid-rendered page block onto the original config.
 *
 * Rendered primitives win, including SDK shorthand that lives on the field
 * itself (`value`, `description`, `button.disabled`) rather than under `props`.
 * Keys the renderer omitted stay on the original config.
 */

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function overlayRenderedValue(
  original: unknown,
  rendered: unknown,
): unknown {
  if (rendered === undefined) return original;
  if (original === undefined) return rendered;

  if (Array.isArray(original) && Array.isArray(rendered)) {
    const length = Math.max(original.length, rendered.length);
    return Array.from({ length }, (_, index) =>
      overlayRenderedValue(original[index], rendered[index]),
    );
  }

  if (isPlainObject(original) && isPlainObject(rendered)) {
    const result: Record<string, unknown> = { ...original };
    for (const key of Object.keys(rendered)) {
      result[key] = overlayRenderedValue(original[key], rendered[key]);
    }
    return result;
  }

  return rendered;
}

/** Drop iterable templates once the server has expanded them into props.items. */
function stripExpandedIterables(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(stripExpandedIterables);
  }
  if (!isPlainObject(value)) {
    return value;
  }

  const result: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value)) {
    result[key] = stripExpandedIterables(entry);
  }

  const props = isPlainObject(result.props) ? result.props : null;
  const expanded = props != null && ("items" in props || "empty" in props);
  if (expanded) {
    delete result.iterable;
    delete result.itemTemplate;
  }

  return result;
}

export function mergeRenderedPageBlock<T>(
  originalBlock: T,
  renderedBlock: unknown,
): T {
  return stripExpandedIterables(
    overlayRenderedValue(originalBlock, renderedBlock),
  ) as T;
}
